import React, { useState, useRef, useEffect, useCallback } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { format, addDays, differenceInDays, startOfMonth, endOfMonth, eachDayOfInterval, startOfQuarter, endOfQuarter, addQuarters, eachWeekOfInterval, eachMonthOfInterval } from 'date-fns';
import Icon from '../../../components/AppIcon';
import { parseLocalDate } from '../../../utils/dateUtils';
import { deliverableService } from '../../../services/deliverableService';

const TIME_SCALE_PIXELS = {
  daily: 24,
  weekly: 16,
  monthly: 8,
  quarterly: 4,
};

const TIME_SCALE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const ItemTypes = { GANTT_BAR: 'gantt_bar' };

const ROW_TYPE_CONFIG = {
  phase: { label: 'Phase', icon: 'GitBranch', color: 'bg-amber-500', textColor: 'text-amber-700', indent: 0 },
  deliverable: { label: 'Deliverable', icon: 'Package', color: 'bg-purple-500', textColor: 'text-purple-700', indent: 0 },
  task: { label: 'Task', icon: 'CheckSquare', color: 'bg-blue-500', textColor: 'text-blue-700', indent: 1 },
  subtask: { label: 'Sub-task', icon: 'CornerDownRight', color: 'bg-green-500', textColor: 'text-green-700', indent: 2 }
};

const DEFAULT_COLUMNS = [
  { id: 'open', label: '', minWidth: 28, defaultWidth: 28 },
  { id: 'type', label: 'Type', minWidth: 60, defaultWidth: 80 },
  { id: 'order', label: 'Order', minWidth: 50, defaultWidth: 65 },
  { id: 'startDate', label: 'Start', minWidth: 60, defaultWidth: 80 },
  { id: 'endDate', label: 'End', minWidth: 60, defaultWidth: 80 },
  { id: 'duration', label: 'Days', minWidth: 40, defaultWidth: 50 },
  { id: 'status', label: 'Status', minWidth: 70, defaultWidth: 90 },
  { id: 'work', label: 'Work', minWidth: 45, defaultWidth: 60 },
  { id: 'resources', label: 'Resources', minWidth: 60, defaultWidth: 80 },
];

const getBarColor = (rowType, status) => {
  if (status === 'completed') return 'bg-green-500';
  if (status === 'blocked') return 'bg-red-500';
  const typeColors = { phase: 'bg-amber-500', deliverable: 'bg-purple-500', task: 'bg-blue-500', subtask: 'bg-emerald-500' };
  return typeColors?.[rowType] || 'bg-gray-400';
};

const getStatusBadge = (status) => {
  const map = {
    'pending': { label: 'Not Started', cls: 'bg-gray-100 text-gray-600' },
    'in-progress': { label: 'In Progress', cls: 'bg-blue-100 text-blue-700' },
    'completed': { label: 'Done', cls: 'bg-green-100 text-green-700' },
    'blocked': { label: 'Blocked', cls: 'bg-red-100 text-red-700' },
    'on_hold': { label: 'On Hold', cls: 'bg-yellow-100 text-yellow-700' },
  };
  return map?.[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-500' };
};

// Column resize handle
const ResizeHandle = ({ onResize }) => {
  const dragging = useRef(false);
  const startX = useRef(0);

  const onMouseDown = (e) => {
    e?.preventDefault();
    dragging.current = true;
    startX.current = e?.clientX;
    const onMouseMove = (ev) => {
      if (!dragging?.current) return;
      const delta = ev?.clientX - startX?.current;
      startX.current = ev?.clientX;
      onResize(delta);
    };
    const onMouseUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div
      onMouseDown={onMouseDown}
      className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize flex items-center justify-center hover:bg-primary/20 group z-10"
      style={{ userSelect: 'none' }}
    >
      <div className="w-0.5 h-4 bg-border group-hover:bg-primary/60 rounded" />
    </div>
  );
};

const ColumnPicker = ({ columns, visibleColumns, onChange, onClose }) => {
  return (
    <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-lg z-50 p-3 w-44">
      <p className="text-xs font-semibold text-foreground mb-2">Show Columns</p>
      {columns?.filter(col => col?.id !== 'open')?.map(col => (
        <label key={col?.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-muted/50 rounded px-1">
          <input
            type="checkbox"
            checked={visibleColumns?.includes(col?.id)}
            onChange={() => onChange(col?.id)}
            className="w-3 h-3 rounded"
          />
          <span className="text-xs text-foreground">{col?.label}</span>
        </label>
      ))}
      <button onClick={onClose} className="mt-2 w-full text-xs text-center text-muted-foreground hover:text-foreground py-1">Close</button>
    </div>
  );
};

// Tooltip component
const GanttTooltip = ({ row, visible, x, y }) => {
  if (!visible || !row) return null;
  const statusBadge = getStatusBadge(row?.status);
  const rowType = row?.rowType || 'deliverable';
  const typeConfig = ROW_TYPE_CONFIG?.[rowType];
  const duration = row?.startDate && row?.endDate
    ? differenceInDays(parseLocalDate(row?.endDate), parseLocalDate(row?.startDate)) + 1
    : 0;

  return (
    <div
      className="fixed z-[9999] bg-popover border border-border rounded-lg shadow-xl p-3 pointer-events-none"
      style={{ left: x + 12, top: y - 10, minWidth: '220px', maxWidth: '280px' }}
    >
      <div className="flex items-start gap-2 mb-2">
        <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
          rowType === 'phase' ? 'bg-amber-500' :
          rowType === 'deliverable' ? 'bg-purple-500' :
          rowType === 'task' ? 'bg-blue-500' : 'bg-emerald-500'
        }`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground leading-tight break-words">{row?.name}</p>
          <span className={`inline-block text-xs px-1.5 py-0.5 rounded mt-1 ${
            rowType === 'phase' ? 'bg-amber-100 text-amber-700' :
            rowType === 'deliverable' ? 'bg-purple-100 text-purple-700' :
            rowType === 'task' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
          }`}>{typeConfig?.label}</span>
        </div>
      </div>
      <div className="space-y-1 text-xs">
        {!row?._isPhase && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Status</span>
            <span className={`px-1.5 py-0.5 rounded ${statusBadge?.cls}`}>{statusBadge?.label}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Start</span>
          <span className="text-foreground font-medium">{row?.startDate ? format(parseLocalDate(row?.startDate), 'MMM d, yyyy') : '—'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">End</span>
          <span className="text-foreground font-medium">{row?.endDate ? format(parseLocalDate(row?.endDate), 'MMM d, yyyy') : '—'}</span>
        </div>
        {duration > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Duration</span>
            <span className="text-foreground font-medium">{duration} day{duration !== 1 ? 's' : ''}</span>
          </div>
        )}
        {row?.assignedResources?.length > 0 && (
          <div className="pt-1 border-t border-border mt-1">
            <span className="text-muted-foreground block mb-1">Resources</span>
            <div className="flex flex-wrap gap-1">
              {row?.assignedResources?.map((r, i) => (
                <span key={i} className="bg-primary/10 text-primary px-1.5 py-0.5 rounded text-xs">{r?.name || r?.resourceName}</span>
              ))}
            </div>
          </div>
        )}
        {(row?.estimatedHours || row?.workEstimate) && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Work</span>
            <span className="text-foreground font-medium">{row?.workEstimate || (row?.estimatedHours ? `${row?.estimatedHours}h` : '—')}</span>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2 pt-1 border-t border-border">Click to edit dates • Drag to reschedule</p>
    </div>
  );
};

// Inline date editor popover
const DateEditPopover = ({ row, x, y, onSave, onClose, saving }) => {
  const [startDate, setStartDate] = useState(row?.startDate ? row?.startDate?.slice(0, 10) : '');
  const [endDate, setEndDate] = useState(row?.endDate ? row?.endDate?.slice(0, 10) : '');
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref?.current && !ref?.current?.contains(e?.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const handleSave = () => {
    if (startDate && endDate) onSave(startDate, endDate);
  };

  // Clamp to viewport
  const style = { position: 'fixed', zIndex: 9999, left: Math.min(x, window.innerWidth - 260), top: Math.min(y, window.innerHeight - 200) };

  return (
    <div ref={ref} className="bg-popover border border-border rounded-lg shadow-xl p-3 w-56" style={style}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-foreground">Edit Date Range</span>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-0.5 rounded">
          <Icon name="X" size={12} />
        </button>
      </div>
      <p className="text-xs text-muted-foreground mb-2 truncate font-medium">{row?.name}</p>
      <div className="space-y-2">
        <div>
          <label className="text-xs text-muted-foreground block mb-0.5">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e?.target?.value)}
            className="w-full text-xs border border-border rounded px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-0.5">End Date</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={e => setEndDate(e?.target?.value)}
            className="w-full text-xs border border-border rounded px-2 py-1 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleSave}
          disabled={saving || !startDate || !endDate}
          className="flex-1 text-xs bg-primary text-primary-foreground rounded px-2 py-1 hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-1"
        >
          {saving ? <Icon name="Loader2" size={11} className="animate-spin" /> : <Icon name="Check" size={11} />}
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onClose} className="flex-1 text-xs border border-border rounded px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-muted">
          Cancel
        </button>
      </div>
    </div>
  );
};

// Inline name editor — renders an input in place of the name text
const InlineNameEditor = ({ row, onSave, onCancel, saving }) => {
  const [value, setValue] = useState(row?.name || '');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef?.current?.focus();
    inputRef?.current?.select();
  }, []);

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') { e?.preventDefault(); if (value?.trim()) onSave(value?.trim()); }
    if (e?.key === 'Escape') onCancel();
  };

  return (
    <div className="flex items-center gap-1 flex-1 min-w-0">
      <input
        ref={inputRef}
        value={value}
        onChange={e => setValue(e?.target?.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (value?.trim() && value?.trim() !== row?.name) onSave(value?.trim()); else onCancel(); }}
        className="flex-1 min-w-0 text-xs border border-primary rounded px-1.5 py-0.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        style={{ fontSize: '11px' }}
        disabled={saving}
        onClick={e => e?.stopPropagation()}
      />
      {saving && <Icon name="Loader2" size={10} className="animate-spin text-muted-foreground flex-shrink-0" />}
    </div>
  );
};

// Inline quick-add row — shown below a parent row when "+" is clicked
const InlineAddRow = ({ parentRow, rowType, indentPx, nameColWidth, onAdd, onCancel, adding }) => {
  const [name, setName] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { inputRef?.current?.focus(); }, []);

  const typeColors = {
    deliverable: 'border-purple-400 bg-purple-50/60',
    task: 'border-blue-400 bg-blue-50/40',
    subtask: 'border-emerald-400 bg-emerald-50/40',
  };
  const typeDot = {
    deliverable: 'bg-purple-500',
    task: 'bg-blue-500',
    subtask: 'bg-emerald-500',
  };
  const typeLabel = { deliverable: 'Deliverable', task: 'Task', subtask: 'Sub-task' };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter') { e?.preventDefault(); if (name?.trim()) onAdd(name?.trim()); }
    if (e?.key === 'Escape') onCancel();
  };

  return (
    <div
      className={`flex border-b border-border ${typeColors?.[rowType] || 'bg-muted/20'} border-l-2`}
      style={{ height: '28px' }}
    >
      <div
        className="flex items-center gap-1 border-r border-border bg-inherit"
        style={{ width: `${nameColWidth}px`, paddingLeft: `${4 + indentPx}px` }}
      >
        <span className="flex-shrink-0 w-4" />
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${typeDot?.[rowType] || 'bg-gray-400'}`} />
        <input
          ref={inputRef}
          value={name}
          onChange={e => setName(e?.target?.value)}
          onKeyDown={handleKeyDown}
          placeholder={`New ${typeLabel?.[rowType] || rowType}…`}
          className="flex-1 min-w-0 text-xs border border-primary rounded px-1.5 py-0.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          style={{ fontSize: '11px' }}
          disabled={adding}
        />
        <button
          onClick={() => { if (name?.trim()) onAdd(name?.trim()); }}
          disabled={adding || !name?.trim()}
          className="flex-shrink-0 p-0.5 rounded hover:bg-primary/20 text-primary disabled:opacity-40"
          title="Add"
        >
          {adding ? <Icon name="Loader2" size={10} className="animate-spin" /> : <Icon name="Check" size={10} />}
        </button>
        <button
          onClick={onCancel}
          className="flex-shrink-0 p-0.5 rounded hover:bg-muted text-muted-foreground"
          title="Cancel"
        >
          <Icon name="X" size={10} />
        </button>
      </div>
    </div>
  );
};

const GanttChartView = ({ deliverables, phases, onDateChange, onEdit, onUpdate, onAddPhase, onEditPhase, project, onReorder, userId, onAddItem, onAdjustDates }) => {
  const projectId = project?.id || 'default';

  const [timeScale, setTimeScale] = useState(() => {
    return localStorage.getItem('gantt_time_scale') || 'weekly';
  });
  const [pixelsPerDay, setPixelsPerDay] = useState(() => {
    const saved = localStorage.getItem('gantt_time_scale') || 'weekly';
    return TIME_SCALE_PIXELS?.[saved] || 24;
  });
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(`gantt_visible_cols_${projectId}`);
      return saved ? JSON.parse(saved) : ['open', 'type', 'order', 'startDate', 'endDate', 'status', 'resources'];
    } catch { return ['open', 'type', 'order', 'startDate', 'endDate', 'status', 'resources']; }
  });
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(`gantt_col_widths_${projectId}`);
      if (saved) return JSON.parse(saved);
    } catch {}
    const w = {};
    DEFAULT_COLUMNS?.forEach(c => { w[c.id] = c?.defaultWidth; });
    return w;
  });
  const [nameColWidth, setNameColWidth] = useState(() => {
    try {
      const saved = localStorage.getItem(`gantt_name_col_width_${projectId}`);
      return saved ? Number(saved) : 200;
    } catch { return 200; }
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => {
    try {
      const saved = localStorage.getItem(`gantt_expanded_ids_${projectId}`);
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });
  const [allExpanded, setAllExpanded] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, row: null, x: 0, y: 0 });
  const [dateEditor, setDateEditor] = useState({ visible: false, row: null, x: 0, y: 0 });
  const [savingDateId, setSavingDateId] = useState(null);
  const [sortBy, setSortBy] = useState('defined_order');
  const [sortLoaded, setSortLoaded] = useState(false);
  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);
  const isSyncingScroll = useRef(false);
  const [dragOverRowId, setDragOverRowId] = useState(null);
  const rowDragItemRef = useRef(null);

  // Inline add state: { parentRow, rowType } or null
  const [inlineAdd, setInlineAdd] = useState(null);
  const [addingInline, setAddingInline] = useState(false);

  // Inline edit state: { row } or null
  const [inlineEdit, setInlineEdit] = useState(null);
  const [savingInlineEdit, setSavingInlineEdit] = useState(false);

  // Touch drag state for mobile long-press
  const touchDragItemRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const [touchDragActive, setTouchDragActive] = useState(false);
  const [touchDragOverId, setTouchDragOverId] = useState(null);

  const syncScrollLeft = (source, target) => {
    if (isSyncingScroll?.current) return;
    isSyncingScroll.current = true;
    if (target?.current) target.current.scrollTop = source?.current?.scrollTop;
    isSyncingScroll.current = false;
  };

  // Dynamic sizing: measure container and auto-adjust pixelsPerDay so timeline fills width
  useEffect(() => {
    if (!wrapperRef?.current) return;
    const observer = new ResizeObserver(() => {
      // Just trigger re-render; actual width used in render
    });
    observer?.observe(wrapperRef?.current);
    return () => observer?.disconnect();
  }, []);

  // Load sort preference from Supabase on mount
  useEffect(() => {
    const loadSortPref = async () => {
      if (!userId || !project?.id) { setSortLoaded(true); return; }
      const { data } = await deliverableService?.getSortPreference(userId, project?.id);
      if (data) setSortBy(data);
      setSortLoaded(true);
    };
    loadSortPref();
  }, [userId, project?.id]);

  // Save sort preference when it changes (after initial load)
  useEffect(() => {
    if (!sortLoaded || !userId || !project?.id) return;
    deliverableService?.saveSortPreference(userId, project?.id, sortBy);
  }, [sortBy, sortLoaded, userId, project?.id]);

  // Persist time scale to localStorage
  useEffect(() => {
    localStorage.setItem('gantt_time_scale', timeScale);
    setPixelsPerDay(TIME_SCALE_PIXELS?.[timeScale] || 24);
  }, [timeScale]);

  // Persist expansion, column visibility, widths
  useEffect(() => {
    localStorage.setItem(`gantt_expanded_ids_${projectId}`, JSON.stringify(Array.from(expandedIds)));
  }, [expandedIds, projectId]);

  useEffect(() => {
    localStorage.setItem(`gantt_visible_cols_${projectId}`, JSON.stringify(visibleColumns));
  }, [visibleColumns, projectId]);

  useEffect(() => {
    localStorage.setItem(`gantt_col_widths_${projectId}`, JSON.stringify(columnWidths));
  }, [columnWidths, projectId]);

  useEffect(() => {
    localStorage.setItem(`gantt_name_col_width_${projectId}`, String(nameColWidth));
  }, [nameColWidth, projectId]);

  if (deliverables?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12 text-center">
        <Icon name="BarChart3" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Items to Display</h3>
        <p className="text-sm text-muted-foreground">Add deliverables, tasks, and sub-tasks to see them in the Gantt chart.</p>
      </div>
    );
  }

  // Sort helper
  const sortItems = (items) => {
    if (!items) return [];
    const sorted = [...items];
    if (sortBy === 'defined_order') {
      sorted?.sort((a, b) => {
        const ao = a?.customOrder ?? Infinity;
        const bo = b?.customOrder ?? Infinity;
        if (ao !== bo) return ao - bo;
        return (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0);
      });
    } else if (sortBy === 'start_date') {
      sorted?.sort((a, b) => {
        const ad = a?.startDate ? parseLocalDate(a?.startDate) : new Date(9999, 0);
        const bd = b?.startDate ? parseLocalDate(b?.startDate) : new Date(9999, 0);
        return ad - bd;
      });
    } else if (sortBy === 'end_date') {
      sorted?.sort((a, b) => {
        const ad = a?.endDate ? parseLocalDate(a?.endDate) : new Date(9999, 0);
        const bd = b?.endDate ? parseLocalDate(b?.endDate) : new Date(9999, 0);
        return ad - bd;
      });
    }
    return sorted;
  };

  const topLevel = sortItems(deliverables?.filter(d => !d?.rowType || d?.rowType === 'deliverable'));
  const tasks = deliverables?.filter(d => d?.rowType === 'task');
  const subtasks = deliverables?.filter(d => d?.rowType === 'subtask');

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id); else next?.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const allIds = new Set([...topLevel?.map(d => d?.id), ...tasks?.map(t => t?.id)]);
    if (phases?.length > 0) phases?.forEach(p => allIds?.add(p?.id));
    allIds?.add('__unphased__');
    setExpandedIds(allIds);
    setAllExpanded(true);
  };

  const collapseAll = () => {
    setExpandedIds(new Set());
    setAllExpanded(false);
  };

  // Build visible rows based on expand state
  const buildVisibleRows = () => {
    const rows = [];

    const addDeliverableRows = (del) => {
      const delTasks = sortItems(tasks?.filter(t => t?.parentId === del?.id));
      const hasChildren = delTasks?.length > 0;
      rows?.push({ ...del, _hasChildren: hasChildren, _level: phases?.length > 0 ? 1 : 0, _parentId: null });
      if (expandedIds?.has(del?.id)) {
        delTasks?.forEach(task => {
          const taskSubs = sortItems(subtasks?.filter(st => st?.parentId === task?.id));
          const hasSubChildren = taskSubs?.length > 0;
          rows?.push({ ...task, _hasChildren: hasSubChildren, _level: phases?.length > 0 ? 2 : 1, _parentId: del?.id });
          if (expandedIds?.has(task?.id)) {
            taskSubs?.forEach(st => {
              rows?.push({ ...st, _hasChildren: false, _level: phases?.length > 0 ? 3 : 2, _parentId: task?.id });
            });
          }
        });
      }
    };

    if (phases?.length > 0) {
      phases?.forEach(phase => {
        const phaseDeliverables = sortItems(topLevel?.filter(d => d?.phaseId === phase?.id));
        const hasChildren = phaseDeliverables?.length > 0;
        rows?.push({
          ...phase,
          rowType: 'phase',
          _isPhase: true,
          _hasChildren: hasChildren,
          _level: 0,
          _parentId: null
        });
        if (expandedIds?.has(phase?.id)) {
          phaseDeliverables?.forEach(addDeliverableRows);
        }
      });
      // Unphased deliverables
      const unphased = sortItems(topLevel?.filter(d => !d?.phaseId || !phases?.find(p => p?.id === d?.phaseId)));
      if (unphased?.length > 0) {
        rows?.push({
          id: '__unphased__',
          name: 'Unassigned',
          rowType: 'phase',
          _isPhase: true,
          _hasChildren: true,
          _level: 0,
          _parentId: null,
          startDate: unphased?.[0]?.startDate,
          endDate: unphased?.[unphased?.length - 1]?.endDate
        });
        if (expandedIds?.has('__unphased__')) {
          unphased?.forEach(addDeliverableRows);
        }
      }
    } else {
      topLevel?.forEach(del => {
        const delTasks = sortItems(tasks?.filter(t => t?.parentId === del?.id));
        const hasChildren = delTasks?.length > 0;
        rows?.push({ ...del, _hasChildren: hasChildren, _level: 0, _parentId: null });
        if (expandedIds?.has(del?.id)) {
          delTasks?.forEach(task => {
            const taskSubs = sortItems(subtasks?.filter(st => st?.parentId === task?.id));
            const hasSubChildren = taskSubs?.length > 0;
            rows?.push({ ...task, _hasChildren: hasSubChildren, _level: 1, _parentId: del?.id });
            if (expandedIds?.has(task?.id)) {
              taskSubs?.forEach(st => {
                rows?.push({ ...st, _hasChildren: false, _level: 2, _parentId: task?.id });
              });
            }
          });
        }
      });
      // Orphaned tasks
      tasks?.filter(t => !topLevel?.find(d => d?.id === t?.parentId))?.forEach(t => rows?.push({ ...t, _hasChildren: false, _level: 0, _parentId: null }));
    }

    return rows;
  };

  const visibleRows = buildVisibleRows();

  // Build project-level row for the Gantt chart
  const projectRow = project ? (() => {
    const allDates = deliverables?.filter(d => d?.startDate || d?.endDate);
    const startDates = allDates?.map(d => d?.startDate)?.filter(Boolean)?.map(d => new Date(d));
    const endDates = allDates?.map(d => d?.endDate)?.filter(Boolean)?.map(d => new Date(d));
    const projStart = project?.startDate || project?.start_date || (startDates?.length > 0 ? format(new Date(Math.min(...startDates)), 'yyyy-MM-dd') : null);
    const projEnd = project?.endDate || project?.end_date || (endDates?.length > 0 ? format(new Date(Math.max(...endDates)), 'yyyy-MM-dd') : null);
    return {
      id: `__project__${project?.id}`,
      name: `${project?.projectNumber ? `#${project?.projectNumber} ` : ''}${project?.projectName || project?.name || 'Project'}`,
      rowType: 'project',
      _isProject: true,
      _hasChildren: false,
      _level: 0,
      startDate: projStart,
      endDate: projEnd,
    };
  })() : null;

  const dates = deliverables?.flatMap(d => [parseLocalDate(d?.startDate), parseLocalDate(d?.endDate)])?.filter(Boolean);
  const minDate = startOfMonth(new Date(Math.min(...dates)));
  const maxDate = endOfMonth(new Date(Math.max(...dates)));
  const days = eachDayOfInterval({ start: minDate, end: maxDate });

  // Build header units based on time scale
  const buildTimeScaleHeaders = () => {
    if (timeScale === 'daily') {
      // Top row: months, bottom row: days
      const monthGroups = [];
      let currentMonth = null;
      let currentCount = 0;
      days?.forEach((day, i) => {
        const monthKey = format(day, 'MMM yyyy');
        if (monthKey !== currentMonth) {
          if (currentMonth) monthGroups?.push({ label: currentMonth, count: currentCount });
          currentMonth = monthKey;
          currentCount = 1;
        } else {
          currentCount++;
        }
        if (i === days?.length - 1) monthGroups?.push({ label: currentMonth, count: currentCount });
      });
      return {
        topGroups: monthGroups,
        bottomUnits: days?.map(day => ({
          label: format(day, 'd'),
          width: pixelsPerDay,
          isWeekend: day?.getDay() === 0 || day?.getDay() === 6,
          date: day,
        })),
      };
    }

    if (timeScale === 'weekly') {
      // Top row: months, bottom row: week start dates
      const weeks = eachWeekOfInterval({ start: minDate, end: maxDate }, { weekStartsOn: 1 });
      const monthGroups = [];
      let currentMonth = null;
      let currentCount = 0;
      weeks?.forEach((weekStart, i) => {
        const monthKey = format(weekStart, 'MMM yyyy');
        if (monthKey !== currentMonth) {
          if (currentMonth) monthGroups?.push({ label: currentMonth, count: currentCount });
          currentMonth = monthKey;
          currentCount = 1;
        } else {
          currentCount++;
        }
        if (i === weeks?.length - 1) monthGroups?.push({ label: currentMonth, count: currentCount });
      });
      const weekWidthPx = pixelsPerDay * 7;
      return {
        topGroups: monthGroups?.map(g => ({ ...g, count: g?.count, widthPx: g?.count * weekWidthPx })),
        bottomUnits: weeks?.map(weekStart => {
          const weekEnd = addDays(weekStart, 6);
          const startMonth = format(weekStart, 'MMM');
          const endMonth = format(weekEnd, 'MMM');
          const label = startMonth === endMonth
            ? `${startMonth} ${format(weekStart, 'd')} - ${format(weekEnd, 'd')}`
            : `${startMonth} ${format(weekStart, 'd')} - ${endMonth} ${format(weekEnd, 'd')}`;
          return {
            label,
            width: weekWidthPx,
            isWeekend: false,
            date: weekStart,
          };
        }),
        unitWidthPx: weekWidthPx,
      };
    }

    if (timeScale === 'monthly') {
      // Top row: years, bottom row: months
      const months = eachMonthOfInterval({ start: minDate, end: maxDate });
      const yearGroups = [];
      let currentYear = null;
      let currentCount = 0;
      months?.forEach((month, i) => {
        const yearKey = format(month, 'yyyy');
        if (yearKey !== currentYear) {
          if (currentYear) yearGroups?.push({ label: currentYear, count: currentCount });
          currentYear = yearKey;
          currentCount = 1;
        } else {
          currentCount++;
        }
        if (i === months?.length - 1) yearGroups?.push({ label: currentYear, count: currentCount });
      });
      const getDaysInMonth = (m) => {
        const end = endOfMonth(m);
        return differenceInDays(end, m) + 1;
      };
      return {
        topGroups: yearGroups,
        bottomUnits: months?.map(month => ({
          label: format(month, 'MMM'),
          width: getDaysInMonth(month) * pixelsPerDay,
          isWeekend: false,
          date: month,
          daysInUnit: getDaysInMonth(month),
        })),
        unitType: 'month',
      };
    }

    if (timeScale === 'quarterly') {
      // Top row: years, bottom row: quarters
      const quarters = [];
      let cur = startOfQuarter(minDate);
      while (cur <= maxDate) {
        quarters?.push(cur);
        cur = startOfQuarter(addQuarters(cur, 1));
      }
      const yearGroups = [];
      let currentYear = null;
      let currentCount = 0;
      quarters?.forEach((q, i) => {
        const yearKey = format(q, 'yyyy');
        if (yearKey !== currentYear) {
          if (currentYear) yearGroups?.push({ label: currentYear, count: currentCount });
          currentYear = yearKey;
          currentCount = 1;
        } else {
          currentCount++;
        }
        if (i === quarters?.length - 1) yearGroups?.push({ label: currentYear, count: currentCount });
      });
      const getQuarterDays = (q) => {
        const end = endOfQuarter(q);
        return differenceInDays(end, q) + 1;
      };
      return {
        topGroups: yearGroups,
        bottomUnits: quarters?.map(q => ({
          label: `Q${Math.ceil((q?.getMonth() + 1) / 3)} ${format(q, 'yyyy')}`,
          width: getQuarterDays(q) * pixelsPerDay,
          isWeekend: false,
          date: q,
          daysInUnit: getQuarterDays(q),
        })),
        unitType: 'quarter',
      };
    }

    return { topGroups: [], bottomUnits: [] };
  };

  const timeScaleHeaders = buildTimeScaleHeaders();

  // Compute total timeline width based on scale
  const totalTimelineWidth = timeScale === 'daily'
    ? days?.length * pixelsPerDay
    : timeScaleHeaders?.bottomUnits?.reduce((sum, u) => sum + u?.width, 0) || 0;

  const toggleColumn = (colId) => {
    setVisibleColumns(prev =>
      prev?.includes(colId) ? prev?.filter(c => c !== colId) : [...prev, colId]
    );
  };

  const handleColResize = (colId, delta, minW) => {
    setColumnWidths(prev => ({
      ...prev,
      [colId]: Math.max(minW, (prev?.[colId] || 80) + delta)
    }));
  };

  const handleNameColResize = (delta) => {
    setNameColWidth(prev => Math.max(120, prev + delta));
  };

  // Total fixed panel width
  const fixedColsWidth = DEFAULT_COLUMNS
    ?.filter(c => visibleColumns?.includes(c?.id))
    ?.reduce((sum, c) => sum + (columnWidths?.[c?.id] || c?.defaultWidth), 0);
  const totalFixedWidth = nameColWidth + fixedColsWidth;

  const ROW_HEIGHT = 28;
  const HEADER_HEIGHT = 44;

  const handleBarMouseEnter = (e, row) => {
    setTooltip({ visible: true, row, x: e?.clientX, y: e?.clientY });
  };

  const handleBarMouseMove = (e) => {
    setTooltip(prev => prev?.visible ? { ...prev, x: e?.clientX, y: e?.clientY } : prev);
  };

  const handleBarMouseLeave = () => {
    setTooltip({ visible: false, row: null, x: 0, y: 0 });
  };

  const handleBarClick = (e, row) => {
    if (row?._isPhase) return;
    e?.stopPropagation();
    setTooltip({ visible: false, row: null, x: 0, y: 0 });
    setDateEditor({ visible: true, row, x: e?.clientX, y: e?.clientY + 10 });
  };

  const handleDateSave = async (startDate, endDate) => {
    if (!dateEditor?.row) return;
    setSavingDateId(dateEditor?.row?.id);
    try {
      await onDateChange(dateEditor?.row?.id, startDate, endDate);
      setDateEditor({ visible: false, row: null, x: 0, y: 0 });
    } finally {
      setSavingDateId(null);
    }
  };

  // Inline add handlers
  const handleInlineAddClick = (e, parentRow, childRowType) => {
    e?.stopPropagation();
    setInlineEdit(null);
    // Auto-expand parent so the inline row appears under it
    if (!expandedIds?.has(parentRow?.id)) {
      setExpandedIds(prev => { const next = new Set(prev); next?.add(parentRow?.id); return next; });
    }
    setInlineAdd({ parentRow, rowType: childRowType });
  };

  const handleInlineAddConfirm = async (name) => {
    if (!name?.trim() || !inlineAdd) return;
    setAddingInline(true);
    try {
      const { parentRow, rowType } = inlineAdd;
      const today = new Date();
      const todayStr = `${today?.getFullYear()}-${String(today?.getMonth() + 1)?.padStart(2, '0')}-${String(today?.getDate())?.padStart(2, '0')}`;
      const parentStartDate = parentRow?.startDate ? parentRow?.startDate?.slice(0, 10) : todayStr;
      const parentEndDate = parentRow?.endDate ? parentRow?.endDate?.slice(0, 10) : todayStr;
      const itemData = {
        name: name?.trim(),
        rowType,
        status: 'pending',
        startDate: parentStartDate,
        endDate: parentEndDate,
      };
      if (rowType === 'deliverable') {
        itemData.phaseId = parentRow?._isPhase && parentRow?.id !== '__unphased__' ? parentRow?.id : null;
      } else if (rowType === 'task') {
        itemData.parentId = parentRow?.id;
        itemData.phaseId = parentRow?.phaseId || null;
      } else if (rowType === 'subtask') {
        itemData.parentId = parentRow?.id;
        itemData.phaseId = parentRow?.phaseId || null;
      }
      await onAddItem?.(itemData);
      setInlineAdd(null);
    } finally {
      setAddingInline(false);
    }
  };

  const handleInlineAddCancel = () => {
    setInlineAdd(null);
    setAddingInline(false);
  };

  // Inline edit handlers
  const handleInlineEditStart = (e, row) => {
    e?.stopPropagation();
    if (row?._isPhase || row?._isProject) return;
    setInlineAdd(null);
    setInlineEdit(row);
  };

  const handleInlineEditSave = async (newName) => {
    if (!inlineEdit || !newName?.trim()) { setInlineEdit(null); return; }
    setSavingInlineEdit(true);
    try {
      await onUpdate?.(inlineEdit?.id, { name: newName?.trim() });
      setInlineEdit(null);
    } finally {
      setSavingInlineEdit(false);
    }
  };

  const handleInlineEditCancel = () => {
    setInlineEdit(null);
    setSavingInlineEdit(false);
  };

  // ── Row reorder helpers (HTML5 native drag, separate from react-dnd bar drag) ──
  const handleRowDragStart = (e, row) => {
    if (row?._isPhase || row?._isProject) { e?.preventDefault(); return; }
    rowDragItemRef.current = row;
    e?.dataTransfer?.setData('text/plain', row?.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleRowDragOver = (e, row) => {
    if (row?._isPhase || row?._isProject) return;
    e?.preventDefault();
    setDragOverRowId(row?.id);
  };

  const handleRowDrop = async (e, targetRow) => {
    e?.preventDefault();
    setDragOverRowId(null);
    const draggedRow = rowDragItemRef?.current;
    if (!draggedRow || draggedRow?.id === targetRow?.id) return;
    if (targetRow?._isPhase || targetRow?._isProject) return;

    const draggedType = draggedRow?.rowType || 'deliverable';
    const targetType = targetRow?.rowType || 'deliverable';
    if (draggedType !== targetType) return;
    if (draggedRow?.parentId !== targetRow?.parentId) return;

    // Get siblings sorted by current order
    const siblings = sortItems(deliverables?.filter(d => {
      const dt = d?.rowType || 'deliverable';
      return dt === draggedType && d?.parentId === draggedRow?.parentId;
    }));

    const dragIdx = siblings?.findIndex(s => s?.id === draggedRow?.id);
    const targetIdx = siblings?.findIndex(s => s?.id === targetRow?.id);
    if (dragIdx === -1 || targetIdx === -1) return;

    const reordered = [...siblings];
    reordered?.splice(dragIdx, 1);
    reordered?.splice(targetIdx, 0, draggedRow);

    const updates = reordered?.map((item, idx) => {
      let newOrder;
      if (draggedType === 'deliverable') {
        newOrder = idx + 1;
      } else if (draggedType === 'task') {
        const parent = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parent?.customOrder != null ? Math.floor(Number(parent?.customOrder)) : 1;
        newOrder = parseFloat(`${parentOrder}.${idx + 1}`);
      } else if (draggedType === 'subtask') {
        const parentTask = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parentTask?.customOrder != null ? Number(parentTask?.customOrder) : 1.1;
        const parentStr = parentOrder?.toFixed(1);
        newOrder = parseFloat(`${parentStr}${idx + 1}`);
      }
      return { id: item?.id, customOrder: newOrder };
    });

    await onReorder?.(updates);
    rowDragItemRef.current = null;
  };

  const handleRowDragEnd = () => {
    setDragOverRowId(null);
    rowDragItemRef.current = null;
  };

  // ── Touch drag helpers (long-press for mobile) ──────────────────────────
  const triggerHaptic = (type = 'light') => {
    if (navigator?.vibrate) {
      navigator.vibrate(type === 'light' ? 30 : type === 'medium' ? 60 : 100);
    }
  };

  const handleTouchStart = useCallback((e, row) => {
    if (row?._isPhase || row?._isProject) return;
    const touch = e?.touches?.[0];
    longPressTimerRef.current = setTimeout(() => {
      touchDragItemRef.current = row;
      setTouchDragActive(true);
      triggerHaptic('medium');
    }, 500);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchDragItemRef?.current) {
      clearTimeout(longPressTimerRef?.current);
      return;
    }
    e?.preventDefault();
    const touch = e?.touches?.[0];
    if (!touch) return;
    const el = document?.elementFromPoint(touch?.clientX, touch?.clientY);
    const rowEl = el?.closest('[data-row-id]');
    const targetId = rowEl?.getAttribute('data-row-id');
    setTouchDragOverId(targetId || null);
  }, []);

  const handleTouchEnd = useCallback(async (e) => {
    clearTimeout(longPressTimerRef?.current);
    if (!touchDragItemRef?.current || !touchDragOverId) {
      touchDragItemRef.current = null;
      setTouchDragActive(false);
      setTouchDragOverId(null);
      return;
    }
    const draggedRow = touchDragItemRef?.current;
    const targetId = touchDragOverId;
    touchDragItemRef.current = null;
    setTouchDragActive(false);
    setTouchDragOverId(null);

    if (draggedRow?.id === targetId) return;
    const targetRow = deliverables?.find(d => d?.id === targetId);
    if (!targetRow) return;

    const draggedType = draggedRow?.rowType || 'deliverable';
    const targetType = targetRow?.rowType || 'deliverable';
    if (draggedType !== targetType) return;
    if (draggedRow?.parentId !== targetRow?.parentId) return;

    const siblings = sortItems(deliverables?.filter(d => {
      const dt = d?.rowType || 'deliverable';
      return dt === draggedType && d?.parentId === draggedRow?.parentId;
    }));
    const dragIdx = siblings?.findIndex(s => s?.id === draggedRow?.id);
    const targetIdx = siblings?.findIndex(s => s?.id === targetRow?.id);
    if (dragIdx === -1 || targetIdx === -1) return;

    const reordered = [...siblings];
    reordered?.splice(dragIdx, 1);
    reordered?.splice(targetIdx, 0, draggedRow);

    const updates = reordered?.map((item, idx) => {
      let newOrder;
      if (draggedType === 'deliverable') {
        newOrder = idx + 1;
      } else if (draggedType === 'task') {
        const parent = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parent?.customOrder != null ? Math.floor(Number(parent?.customOrder)) : 1;
        newOrder = parseFloat(`${parentOrder}.${idx + 1}`);
      } else if (draggedType === 'subtask') {
        const parentTask = deliverables?.find(d => d?.id === item?.parentId);
        const parentOrder = parentTask?.customOrder != null ? Number(parentTask?.customOrder) : 1.1;
        const parentStr = parentOrder?.toFixed(1);
        newOrder = parseFloat(`${parentStr}${idx + 1}`);
      }
      return { id: item?.id, customOrder: newOrder };
    });

    triggerHaptic('light');
    await onReorder?.(updates);
  }, [touchDragOverId, deliverables, onReorder]);

  // Determine what inline add row type to show after a given row
  // Returns the inline add row JSX if this row is the parent of the current inlineAdd
  const renderInlineAddAfterRow = (row) => {
    if (!inlineAdd) return null;
    const { parentRow, rowType } = inlineAdd;
    if (parentRow?.id !== row?.id) return null;
    const indentForType = {
      deliverable: (phases?.length > 0 ? 1 : 0) * 14,
      task: (phases?.length > 0 ? 2 : 1) * 14,
      subtask: (phases?.length > 0 ? 3 : 2) * 14,
    };
    return (
      <InlineAddRow
        key={`inline-add-${row?.id}`}
        parentRow={parentRow}
        rowType={rowType}
        indentPx={indentForType?.[rowType] || 0}
        nameColWidth={nameColWidth}
        onAdd={handleInlineAddConfirm}
        onCancel={handleInlineAddCancel}
        adding={addingInline}
      />
    );
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div ref={wrapperRef} className="bg-card rounded-lg border border-border overflow-hidden w-full">
        {/* Toolbar */}
        <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-muted/20">
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted border border-border"
            >
              <Icon name="ChevronDown" size={12} />
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted border border-border"
            >
              <Icon name="ChevronRight" size={12} />
              Collapse All
            </button>
            <span className="text-xs text-muted-foreground ml-1">{visibleRows?.length} rows</span>
            {/* Sort By */}
            <div className="flex items-center gap-1 ml-2">
              <Icon name="ArrowUpDown" size={12} className="text-muted-foreground" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e?.target?.value)}
                className="text-xs bg-input border border-border rounded px-2 py-0.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring min-w-[110px]"
              >
                <option value="defined_order">Defined Order</option>
                <option value="start_date">Start Date</option>
                <option value="end_date">End Date</option>
              </select>
              {/* Active sort badge */}
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border ${
                sortBy === 'defined_order' ?'bg-purple-100 text-purple-700 border-purple-200'
                  : sortBy === 'start_date' ?'bg-blue-100 text-blue-700 border-blue-200' :'bg-amber-100 text-amber-700 border-amber-200'
              }`}>
                <Icon name="Check" size={10} />
                {sortBy === 'defined_order' ? 'Defined' : sortBy === 'start_date' ? 'Start Date' : 'End Date'}
              </span>
            </div>
            {/* Time Scale Selector */}
            <div className="flex items-center gap-1 ml-2">
              <Icon name="Calendar" size={12} className="text-muted-foreground" />
              <select
                value={timeScale}
                onChange={(e) => setTimeScale(e?.target?.value)}
                className="text-xs bg-input border border-border rounded px-2 py-0.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring min-w-[100px]"
              >
                {TIME_SCALE_OPTIONS?.map(opt => (
                  <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
                ))}
              </select>
            </div>
            {/* Adjust Dates button */}
            {onAdjustDates && (
              <button
                onClick={onAdjustDates}
                className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 px-2 py-1 rounded hover:bg-accent/10 border border-accent/30 transition-colors ml-1"
                title="Adjust all project dates"
              >
                <Icon name="CalendarClock" size={12} />
                Adjust Dates
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground hidden sm:inline">Hover for details • Click bar to edit dates • Double-click name to edit</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPixelsPerDay(prev => Math.max(prev - 8, 4))}
                className="p-1 rounded hover:bg-muted border border-border text-muted-foreground hover:text-foreground"
                title="Zoom Out"
              >
                <Icon name="ZoomOut" size={13} />
              </button>
              <span className="text-xs text-muted-foreground w-8 text-center">{pixelsPerDay}px</span>
              <button
                onClick={() => setPixelsPerDay(prev => Math.min(prev + 8, 80))}
                className="p-1 rounded hover:bg-muted border border-border text-muted-foreground hover:text-foreground"
                title="Zoom In"
              >
                <Icon name="ZoomIn" size={13} />
              </button>
            </div>
            <div className="relative">
              <button
                onClick={() => setShowColumnPicker(prev => !prev)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded hover:bg-muted border border-border"
              >
                <Icon name="Columns" size={12} />
                Columns
              </button>
              {showColumnPicker && (
                <ColumnPicker
                  columns={DEFAULT_COLUMNS}
                  visibleColumns={visibleColumns}
                  onChange={toggleColumn}
                  onClose={() => setShowColumnPicker(false)}
                />
              )}
            </div>
          </div>
        </div>

        {/* Chart: fixed left panel + scrollable right timeline */}
        <div className="flex" style={{ maxHeight: 'calc(100vh - 280px)' }}>

          {/* LEFT PANEL — fixed width, vertical scroll only */}
          <div
            ref={leftPanelRef}
            className="flex-shrink-0 overflow-y-auto overflow-x-hidden border-r border-border"
            style={{ width: `${totalFixedWidth}px` }}
            onScroll={() => syncScrollLeft(leftPanelRef, rightPanelRef)}
          >
            {/* Left header */}
            <div className="flex border-b border-border sticky top-0 bg-card z-20" style={{ height: `${HEADER_HEIGHT}px` }}>
              {/* Name col header */}
              <div
                className="flex-shrink-0 flex items-end pb-1 border-r border-border bg-muted px-2 relative"
                style={{ width: `${nameColWidth}px` }}
              >
                <span className="text-xs font-semibold text-foreground">Name</span>
                <ResizeHandle onResize={handleNameColResize} />
              </div>
              {/* Optional col headers */}
              {DEFAULT_COLUMNS?.filter(c => visibleColumns?.includes(c?.id))?.map(col => (
                <div
                  key={col?.id}
                  className="flex-shrink-0 flex items-end pb-1 border-r border-border bg-muted px-1 relative"
                  style={{ width: `${columnWidths?.[col?.id] || col?.defaultWidth}px` }}
                >
                  <span className="text-xs font-semibold text-foreground">{col?.label}</span>
                  <ResizeHandle onResize={(delta) => handleColResize(col?.id, delta, col?.minWidth)} />
                </div>
              ))}
            </div>

            {/* Left rows */}
            {[...(projectRow ? [projectRow] : []), ...visibleRows]?.map((row) => {
              const rowType = row?.rowType || 'deliverable';
              const typeConfig = ROW_TYPE_CONFIG?.[rowType];
              const indentPx = row?._level * 14;
              const hasChildren = row?._hasChildren;
              const isExpanded = expandedIds?.has(row?.id);
              const rowBg = row?._isProject ? 'bg-primary/10 hover:bg-primary/15'
                : rowType === 'deliverable' ? 'bg-purple-50/40 hover:bg-purple-50/70'
                : rowType === 'task' ? 'bg-blue-50/20 hover:bg-blue-50/50'
                : rowType === 'phase'? 'bg-amber-50/60 hover:bg-amber-50/80' :'bg-white hover:bg-gray-50';

              const statusBadge = getStatusBadge(row?.status);
              const colVisible = (id) => visibleColumns?.includes(id);
              const colWidth = (id) => columnWidths?.[id] || DEFAULT_COLUMNS?.find(c => c?.id === id)?.defaultWidth || 80;
              const duration = row?.startDate && row?.endDate
                ? differenceInDays(parseLocalDate(row?.endDate), parseLocalDate(row?.startDate)) + 1
                : 0;

              // Format order display
              const orderDisplay = (() => {
                if (row?.customOrder == null) return '—';
                const n = Number(row?.customOrder);
                if (Number.isInteger(n)) return String(n);
                const parts = n?.toFixed(10)?.replace(/0+$/, '')?.split('.');
                const intPart = parts?.[0];
                const decPart = parts?.[1] || '';
                if (decPart?.length >= 2) {
                  return `${intPart}.${decPart?.[0]}.${decPart?.slice(1)}`;
                }
                return `${intPart}.${decPart}`;
              })();

              const isDragOver = dragOverRowId === row?.id || touchDragOverId === row?.id;
              const isDraggable = !row?._isPhase && !row?._isProject;
              const isEditingName = inlineEdit?.id === row?.id;

              // Determine what "+" button to show and what child type it creates
              const addButtonConfig = (() => {
                if (row?._isProject) return null;
                if (row?._isPhase) return { childType: 'deliverable', title: 'Add deliverable', color: 'text-purple-600 hover:bg-purple-100' };
                if (rowType === 'deliverable') return { childType: 'task', title: 'Add task', color: 'text-blue-600 hover:bg-blue-100' };
                if (rowType === 'task') return { childType: 'subtask', title: 'Add sub-task', color: 'text-emerald-600 hover:bg-emerald-100' };
                return null;
              })();

              return (
                <React.Fragment key={row?.id}>
                  <div
                    data-row-id={row?.id}
                    className={`flex border-b border-border ${rowBg} group/namerow ${isDragOver ? 'border-t-2 border-t-primary' : ''} ${touchDragActive && touchDragItemRef?.current?.id === row?.id ? 'opacity-50' : ''}`}
                    style={{ height: `${ROW_HEIGHT}px` }}
                    draggable={isDraggable}
                    onDragStart={isDraggable ? (e) => handleRowDragStart(e, row) : undefined}
                    onDragOver={isDraggable ? (e) => handleRowDragOver(e, row) : undefined}
                    onDrop={isDraggable ? (e) => handleRowDrop(e, row) : undefined}
                    onDragEnd={isDraggable ? handleRowDragEnd : undefined}
                    onTouchStart={isDraggable ? (e) => handleTouchStart(e, row) : undefined}
                    onTouchMove={isDraggable ? handleTouchMove : undefined}
                    onTouchEnd={isDraggable ? handleTouchEnd : undefined}
                  >
                    {/* Name column */}
                    <div
                      className="flex-shrink-0 flex items-center border-r border-border bg-inherit gap-1"
                      style={{ width: `${nameColWidth}px`, paddingLeft: `${4 + indentPx}px` }}
                    >
                      {/* Drag handle */}
                      {isDraggable && (
                        <span className="flex-shrink-0 opacity-0 group-hover/namerow:opacity-60 cursor-grab active:cursor-grabbing text-muted-foreground mr-0.5" title="Drag to reorder">
                          <Icon name="GripVertical" size={11} />
                        </span>
                      )}
                      {hasChildren ? (
                        <button
                          onClick={() => toggleExpand(row?.id)}
                          className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded hover:bg-muted text-muted-foreground"
                        >
                          <Icon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} size={11} />
                        </button>
                      ) : (
                        <span className="flex-shrink-0 w-4" />
                      )}
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        row?._isProject ? 'bg-primary' :
                        rowType === 'phase' ? 'bg-amber-500' :
                        rowType === 'deliverable' ? 'bg-purple-500' :
                        rowType === 'task' ? 'bg-blue-500' : 'bg-emerald-500'
                      }`} />

                      {/* Name: inline editor or text */}
                      {isEditingName ? (
                        <InlineNameEditor
                          row={row}
                          onSave={handleInlineEditSave}
                          onCancel={handleInlineEditCancel}
                          saving={savingInlineEdit}
                        />
                      ) : (
                        <span
                          className={`text-xs truncate font-medium flex-1 transition-colors ${row?._isProject ? 'text-primary font-semibold cursor-default' : 'text-foreground cursor-pointer hover:text-primary'}`}
                          title={row?._isProject ? row?.name : `${row?.name} — double-click to rename`}
                          onDoubleClick={!row?._isProject && !row?._isPhase ? (e) => handleInlineEditStart(e, row) : undefined}
                        >
                          {row?.name}
                        </span>
                      )}

                      {/* Action buttons — visible on hover */}
                      {!isEditingName && (
                        <div className="flex items-center gap-0.5 opacity-0 group-hover/namerow:opacity-100 transition-opacity flex-shrink-0 mr-1">
                          {/* "+" button to add child */}
                          {addButtonConfig && onAddItem && (
                            <button
                              onClick={(e) => handleInlineAddClick(e, row, addButtonConfig?.childType)}
                              title={addButtonConfig?.title}
                              className={`p-0.5 rounded transition-colors ${addButtonConfig?.color}`}
                            >
                              <Icon name="Plus" size={11} />
                            </button>
                          )}
                          {/* Edit name button (pencil) for non-phase, non-project rows */}
                          {!row?._isPhase && !row?._isProject && (
                            <button
                              onClick={(e) => handleInlineEditStart(e, row)}
                              title="Rename"
                              className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <Icon name="Pencil" size={10} />
                            </button>
                          )}
                          {/* Phase-specific buttons */}
                          {row?._isPhase && row?.id !== '__unphased__' && (
                            <>
                              <button
                                onClick={(e) => { e?.stopPropagation(); onAddPhase && onAddPhase(); }}
                                title="Add new phase"
                                className="p-0.5 rounded hover:bg-amber-200 text-amber-700 transition-colors"
                              >
                                <Icon name="Plus" size={11} />
                              </button>
                              <button
                                onClick={(e) => { e?.stopPropagation(); onEditPhase && onEditPhase(row); }}
                                title="Edit phase"
                                className="p-0.5 rounded hover:bg-amber-200 text-amber-700 transition-colors"
                              >
                                <Icon name="Pencil" size={11} />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Optional columns */}
                    {colVisible('open') && (
                      <div className="flex-shrink-0 flex items-center justify-center border-r border-border px-0.5" style={{ width: `${colWidth('open')}px` }}>
                        {!row?._isPhase && (
                          <button
                            onClick={(e) => { e?.stopPropagation(); onEdit(row); }}
                            title="Open deliverable"
                            className="p-0.5 rounded hover:bg-primary/10 text-primary transition-colors"
                          >
                            <Icon name="ExternalLink" size={10} />
                          </button>
                        )}
                      </div>
                    )}
                    {colVisible('type') && (
                      <div className="flex-shrink-0 flex items-center justify-center border-r border-border px-1" style={{ width: `${colWidth('type')}px` }}>
                        <span className={`text-xs px-1 rounded font-medium ${
                          rowType === 'phase' ? 'bg-amber-100 text-amber-700' :
                          rowType === 'deliverable' ? 'bg-purple-100 text-purple-700' :
                          rowType === 'task' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`} style={{ fontSize: '10px' }}>{typeConfig?.label}</span>
                      </div>
                    )}
                    {colVisible('order') && (
                      <div className="flex-shrink-0 flex items-center justify-center border-r border-border px-1" style={{ width: `${colWidth('order')}px` }}>
                        {!row?._isPhase && !row?._isProject && (
                          <span className="font-mono text-muted-foreground" style={{ fontSize: '10px' }}>{orderDisplay}</span>
                        )}
                      </div>
                    )}
                    {colVisible('startDate') && (
                      <div className="flex-shrink-0 flex items-center border-r border-border px-1" style={{ width: `${colWidth('startDate')}px` }}>
                        <span className="text-muted-foreground" style={{ fontSize: '10px' }}>{row?.startDate ? format(parseLocalDate(row?.startDate), 'MMM d') : '—'}</span>
                      </div>
                    )}
                    {colVisible('endDate') && (
                      <div className="flex-shrink-0 flex items-center border-r border-border px-1" style={{ width: `${colWidth('endDate')}px` }}>
                        <span className="text-muted-foreground" style={{ fontSize: '10px' }}>{row?.endDate ? format(parseLocalDate(row?.endDate), 'MMM d') : '—'}</span>
                      </div>
                    )}
                    {colVisible('duration') && (
                      <div className="flex-shrink-0 flex items-center justify-center border-r border-border px-1" style={{ width: `${colWidth('duration')}px` }}>
                        <span className="text-muted-foreground" style={{ fontSize: '10px' }}>{duration > 0 ? `${duration}d` : '—'}</span>
                      </div>
                    )}
                    {colVisible('status') && (
                      <div className="flex-shrink-0 flex items-center border-r border-border px-1" style={{ width: `${colWidth('status')}px` }}>
                        {!row?._isPhase && (
                          <span className={`px-1 rounded ${statusBadge?.cls}`} style={{ fontSize: '10px' }}>{statusBadge?.label}</span>
                        )}
                      </div>
                    )}
                    {colVisible('work') && (
                      <div className="flex-shrink-0 flex items-center border-r border-border px-1" style={{ width: `${colWidth('work')}px` }}>
                        <span className="text-muted-foreground" style={{ fontSize: '10px' }}>{row?.workEstimate || (row?.estimatedHours ? `${row?.estimatedHours}h` : '—')}</span>
                      </div>
                    )}
                    {colVisible('resources') && (
                      <div className="flex-shrink-0 flex items-center border-r border-border px-1 gap-0.5" style={{ width: `${colWidth('resources')}px` }}>
                        {row?.assignedResources?.slice(0, 3)?.map((r, i) => (
                          <div key={i} className="w-4 h-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center" title={r?.name}>
                            <span style={{ fontSize: '9px' }}>{r?.name?.charAt(0)}</span>
                          </div>
                        ))}
                        {(row?.assignedResources?.length || 0) > 3 && (
                          <span className="text-muted-foreground" style={{ fontSize: '10px' }}>+{row?.assignedResources?.length - 3}</span>
                        )}
                        {!row?.assignedResources?.length && <span className="text-muted-foreground" style={{ fontSize: '10px' }}>—</span>}
                      </div>
                    )}
                  </div>
                  {/* Inline add row — rendered immediately after the parent row */}
                  {renderInlineAddAfterRow(row)}
                </React.Fragment>
              );
            })}
          </div>

          {/* RIGHT PANEL — timeline, horizontal + vertical scroll */}
          <div
            ref={rightPanelRef}
            className="flex-1 overflow-x-auto overflow-y-auto min-w-0"
            onScroll={() => syncScrollLeft(rightPanelRef, leftPanelRef)}
          >
            {/* Timeline header */}
            <div className="flex flex-col border-b border-border sticky top-0 bg-card z-20" style={{ height: `${HEADER_HEIGHT}px`, minWidth: `${totalTimelineWidth}px` }}>
              {/* Top row: month/year/quarter groupings */}
              <div className="flex border-b border-border bg-muted/80" style={{ height: '20px' }}>
                {timeScaleHeaders?.topGroups?.map((mg, i) => {
                  const startIdx = timeScaleHeaders?.topGroups?.slice(0, i)?.reduce((s, g) => s + g?.count, 0);
                  const groupWidth = timeScaleHeaders?.bottomUnits?.slice(startIdx, startIdx + mg?.count)?.reduce((s, u) => s + u?.width, 0) || mg?.count * pixelsPerDay;
                  return (
                    <div
                      key={i}
                      className="flex-shrink-0 flex items-center justify-center border-r border-border"
                      style={{ width: `${groupWidth}px` }}
                    >
                      <span className="text-xs font-semibold text-foreground truncate px-1">{mg?.label}</span>
                    </div>
                  );
                })}
              </div>
              {/* Bottom row: day/week/month/quarter units */}
              <div className="flex bg-muted" style={{ height: '24px' }}>
                {timeScaleHeaders?.bottomUnits?.map((unit, index) => (
                  <div
                    key={index}
                    className={`flex-shrink-0 flex items-center justify-center border-r border-border ${
                      unit?.isWeekend ? 'bg-muted/60' : 'bg-muted/20'
                    }`}
                    style={{ width: `${unit?.width}px` }}
                  >
                    <span className="text-xs text-muted-foreground truncate px-0.5" style={{ fontSize: '10px' }}>
                      {timeScale === 'daily' && pixelsPerDay >= 16 ? unit?.label :
                       timeScale === 'weekly' ? (unit?.width >= 60 ? unit?.label : format(unit?.date, 'MMM d')) :
                       timeScale === 'monthly' ? unit?.label :
                       timeScale === 'quarterly' ? unit?.label : unit?.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline rows */}
            {[...(projectRow ? [projectRow] : []), ...visibleRows]?.map((row) => {
              const rowType = row?.rowType || 'deliverable';
              const rowBg = row?._isProject ? 'bg-primary/10 hover:bg-primary/15'
                : rowType === 'deliverable' ? 'bg-purple-50/40 hover:bg-purple-50/70'
                : rowType === 'task' ? 'bg-blue-50/20 hover:bg-blue-50/50'
                : rowType === 'phase'? 'bg-amber-50/60 hover:bg-amber-50/80' :'bg-white hover:bg-gray-50';

              return (
                <React.Fragment key={row?.id}>
                  <DropRow
                    row={row}
                    rowBg={rowBg}
                    rowType={rowType}
                    typeConfig={ROW_TYPE_CONFIG?.[rowType]}
                    onDateChange={onDateChange}
                    onEdit={onEdit}
                    onUpdate={onUpdate}
                    pixelsPerDay={pixelsPerDay}
                    minDate={minDate}
                    days={days}
                    rowHeight={ROW_HEIGHT}
                    onBarMouseEnter={handleBarMouseEnter}
                    onBarMouseMove={handleBarMouseMove}
                    onBarMouseLeave={handleBarMouseLeave}
                    onBarClick={handleBarClick}
                    savingDateId={savingDateId}
                  />
                  {/* Spacer row in timeline to match inline add row in left panel */}
                  {inlineAdd?.parentRow?.id === row?.id && (
                    <div
                      className="border-b border-border bg-muted/10"
                      style={{ height: '28px', minWidth: `${totalTimelineWidth}px` }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="px-3 py-1.5 border-t border-border bg-muted/20 flex flex-wrap items-center gap-4 text-xs">
          {phases?.length > 0 && (
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-muted-foreground">Phase</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-purple-500"></div>
            <span className="text-muted-foreground">Deliverable</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
            <span className="text-muted-foreground">Task</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-muted-foreground">Sub-task</span>
          </div>
          <div className="flex items-center gap-1.5 ml-2">
            <div className="w-3 h-3 rounded bg-green-500"></div>
            <span className="text-muted-foreground">Completed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-red-500"></div>
            <span className="text-muted-foreground">Blocked</span>
          </div>
          <div className="flex items-center gap-1.5 ml-auto text-muted-foreground">
            <Icon name="Plus" size={10} />
            <span>Hover row to add child • Double-click name to rename</span>
          </div>
        </div>
      </div>
      {/* Tooltip */}
      <GanttTooltip
        row={tooltip?.row}
        visible={tooltip?.visible}
        x={tooltip?.x}
        y={tooltip?.y}
      />
      {/* Date editor popover */}
      {dateEditor?.visible && (
        <DateEditPopover
          row={dateEditor?.row}
          x={dateEditor?.x}
          y={dateEditor?.y}
          onSave={handleDateSave}
          onClose={() => setDateEditor({ visible: false, row: null, x: 0, y: 0 })}
          saving={savingDateId === dateEditor?.row?.id}
        />
      )}
    </DndProvider>
  );
};

// Separate component to use useDrop hook properly — now only renders the timeline bar area
const DropRow = ({
  row, rowBg, rowType, onDateChange, onEdit, onUpdate, pixelsPerDay, minDate, days, rowHeight,
  onBarMouseEnter, onBarMouseMove, onBarMouseLeave, onBarClick, savingDateId
}) => {
  const rowRef = useRef(null);

  const [collected, drag] = useDrag(() => ({
    type: ItemTypes?.GANTT_BAR,
    item: { deliverable: row },
    collect: (monitor) => ({ isDragging: !!monitor?.isDragging() })
  }), [row]);

  const [, drop] = useDrop(() => ({
    accept: ItemTypes?.GANTT_BAR,
    drop: (item, monitor) => {
      const offset = monitor?.getClientOffset();
      const rowRect = rowRef?.current?.getBoundingClientRect();
      if (!offset || !rowRect) return;
      if (item?.deliverable?.id !== row?.id) return;
      const relativeX = offset?.x - rowRect?.left;
      const daysDiff = Math.round(relativeX / pixelsPerDay);
      const newStartDate = addDays(minDate, daysDiff);
      const dur = differenceInDays(parseLocalDate(item?.deliverable?.endDate), parseLocalDate(item?.deliverable?.startDate));
      const newEndDate = addDays(newStartDate, dur);
      onDateChange(row?.id, format(newStartDate, 'yyyy-MM-dd'), format(newEndDate, 'yyyy-MM-dd'));
    }
  }), [row, onDateChange, pixelsPerDay, minDate]);

  const startDiff = row?.startDate ? differenceInDays(parseLocalDate(row?.startDate), minDate) : 0;
  const barDuration = row?.startDate && row?.endDate
    ? differenceInDays(parseLocalDate(row?.endDate), parseLocalDate(row?.startDate)) + 1
    : 1;
  const barLeft = startDiff * pixelsPerDay;
  const barWidth = Math.max(barDuration * pixelsPerDay, 16);
  const barColor = getBarColor(rowType, row?.status);
  const isSaving = savingDateId === row?.id;
  const isProject = row?._isProject;

  return (
    <div
      className={`flex border-b border-border ${rowBg} ${collected?.isDragging ? 'opacity-50' : ''}`}
      style={{ height: `${rowHeight}px` }}
    >
      {/* Gantt bar area */}
      <div
        ref={(node) => { rowRef.current = node; drop(node); }}
        className="relative"
        style={{ height: `${rowHeight}px`, width: `${days?.length * pixelsPerDay}px` }}
      >
        {/* Weekend shading */}
        {days?.map((day, i) => {
          const isWeekend = day?.getDay() === 0 || day?.getDay() === 6;
          return isWeekend ? (
            <div key={i} className="absolute top-0 bottom-0 bg-muted/20" style={{ left: `${i * pixelsPerDay}px`, width: `${pixelsPerDay}px` }} />
          ) : null;
        })}
        {row?.startDate && row?.endDate && (
          <div
            ref={isProject ? null : drag}
            className={`absolute rounded flex items-center px-1.5 text-white shadow transition-all group/bar ${
              isProject ? 'bg-primary cursor-default' : `${barColor} ${row?._isPhase ? 'cursor-default' : 'cursor-move hover:brightness-110 hover:scale-y-105'}`
            } ${isSaving ? 'opacity-60' : ''}`}
            style={{ left: `${barLeft}px`, width: `${barWidth}px`, top: isProject ? '3px' : '4px', height: isProject ? '22px' : '20px' }}
            onMouseEnter={(e) => !isProject && onBarMouseEnter(e, row)}
            onMouseMove={!isProject ? onBarMouseMove : undefined}
            onMouseLeave={!isProject ? onBarMouseLeave : undefined}
            onClick={(e) => !row?._isPhase && !isProject && onBarClick(e, row)}
          >
            <span className="truncate" style={{ fontSize: '10px' }}>{row?.name}</span>
            {!row?._isPhase && !isProject && (
              <div className="ml-auto flex-shrink-0 flex items-center gap-0.5 opacity-0 group-hover/bar:opacity-100 transition-opacity">
                {isSaving ? (
                  <Icon name="Loader2" size={9} className="animate-spin" />
                ) : (
                  <button
                    onClick={(e) => { e?.stopPropagation(); onEdit(row); }}
                    className="hover:bg-white/20 rounded p-0.5"
                    title="Open editor"
                  >
                    <Icon name="Edit2" size={9} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GanttChartView;